import dayjs, {Dayjs} from "dayjs";
import {
    AccommodationTaxScheme,
    EventFeeTypeCreateDtoInput,
    EventFeeTypeReturnDto,
    EventFeeTypeUpdateDtoInput,
    EventHotelCreateDtoInput,
    EventHotelReturnDto,
    EventPricePeriodCreateDtoInput,
    EventPricePeriodReturnDto,
    EventTaxSettingReturnDto,
    PaymentMethodCategory
} from "../../../GraphQL/generated/graphql";
import {percentToTaxRate, taxRateToPercent} from "../generalSettingsPageHelpers";
import {EventPublicAssetModeValue, normalizeEventPublicAssetMode} from "../../../eventPublicAssetMode";
import {atiColors} from "../../../Theme/atiTheme";
import type {
    EventAccessGrantRow,
    EventAccessUserRow,
    EventBasicSetup,
    EventBasicSetupBranding,
    EventPaymentMethodCategory,
    EventPaymentMethodConfig
} from "./eventConfigQueries";

export const DEFAULT_PRIMARY_COLOR = atiColors.blue;
export const DEFAULT_SECONDARY_COLOR = atiColors.orange;
export const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export const requiredRule = (messageText: string) => ({required: true, message: messageText});

export const textValue = (value?: string | null) => value ?? "";

export const firstNonEmptyText = (...values: Array<string | null | undefined>) =>
    values.map(value => textValue(value).trim()).find(Boolean) ?? "";

const joinNameParts = (...parts: Array<string | null | undefined>) =>
    parts.map(part => textValue(part).trim()).filter(Boolean).join(" ");

export const getEventAccessUserLabel = (user: EventAccessUserRow) =>
    firstNonEmptyText(joinNameParts(user.firstName, user.lastName), user.email, user.userName, user.id);

export const getEventAccessGrantLabel = (grant: EventAccessGrantRow) =>
    firstNonEmptyText(grant.userEmail, grant.userName, grant.userId);

export const formTextValue = (value: unknown, fallback?: string | null) =>
    value === undefined || value === null ? textValue(fallback) : textValue(String(value));

export const formBooleanValue = (value: unknown, fallback = false) =>
    value === undefined || value === null || value === ""
        ? fallback
        : value === true || value === "true";

export const dateInputValue = (value?: string | null) => {
    if (!value) {
        return "";
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const findById = <TItem extends { id: number }>(
    items: Array<TItem | null | undefined>,
    id: unknown
): TItem | undefined => {
    const numericId = Number(id);
    return Number.isInteger(numericId)
        ? items.find(item => item?.id === numericId) ?? undefined
        : undefined;
};

// Branding colours

export const normalizeHexColor = (value?: string | null) => {
    const trimmed = textValue(value).trim();
    if (!trimmed) {
        return "";
    }

    const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
    return HEX_COLOR_PATTERN.test(withHash) ? withHash.toUpperCase() : trimmed;
};

const colorValueOrDefault = (value: string | null | undefined, fallback: string) =>
    normalizeHexColor(value) || fallback;

export type BrandingColorLabels = {
    primaryColor: string;
    secondaryColor: string;
    requiredMessage: (label: string) => string;
    invalidMessage: (label: string) => string;
};

export const colorRules = (label: string, labels?: BrandingColorLabels) => [
    requiredRule(labels?.requiredMessage(label) ?? `${label} is required.`),
    {
        validator: (_: unknown, value?: string) => {
            const normalized = normalizeHexColor(value);
            return !normalized || HEX_COLOR_PATTERN.test(normalized)
                ? Promise.resolve()
                : Promise.reject(new Error(labels?.invalidMessage(label) ?? `${label} must be a hex color like #223F6B.`));
        }
    }
];

// Event basics

export type EventBasicSetupFormValues = {
    code: string;
    name: string;
    nameHr: string;
    nameEn: string;
    place: string;
    address: string;
    eventInformationHr: string;
    eventInformationEn: string;
    onSiteRegistrationInstructionsHr: string;
    onSiteRegistrationInstructionsEn: string;
    hasParticipationFee: boolean;
    startDate?: Dayjs;
    endDate?: Dayjs;
    logoUrl: string;
    primaryColor: string;
    secondaryColor: string;
    publicAssetMode: EventPublicAssetModeValue;
    programPdfUrl: string;
    venueLayoutImageUrl: string;
    sponsorsImageUrl: string;
    badgeTemplateUrl: string;
    badgeBackTemplateUrl: string;
    badgeFirstNameX?: number | null;
    badgeFirstNameY?: number | null;
    badgeLastNameX?: number | null;
    badgeLastNameY?: number | null;
    badgeCompanyX?: number | null;
    badgeCompanyY?: number | null;
    badgeQrCodeX?: number | null;
    badgeQrCodeY?: number | null;
    certificateTemplateUrl: string;
    certificateParticipantNameX?: number | null;
    certificateParticipantNameY?: number | null;
};

export const eventBasicSetupToFormValues = (
    event: EventBasicSetup,
    branding?: EventBasicSetupBranding | null
): EventBasicSetupFormValues => ({
    code: textValue(event.code),
    name: textValue(event.name),
    nameHr: textValue(event.nameHr),
    nameEn: textValue(event.nameEn),
    place: textValue(event.place),
    address: textValue(event.address),
    eventInformationHr: textValue(event.eventInformationHr),
    eventInformationEn: textValue(event.eventInformationEn),
    onSiteRegistrationInstructionsHr: textValue(event.onSiteRegistrationInstructionsHr),
    onSiteRegistrationInstructionsEn: textValue(event.onSiteRegistrationInstructionsEn),
    hasParticipationFee: event.hasParticipationFee ?? true,
    startDate: event.startDate ? dayjs(event.startDate) : undefined,
    endDate: event.endDate ? dayjs(event.endDate) : undefined,
    logoUrl: textValue(branding?.logoUrl),
    primaryColor: colorValueOrDefault(branding?.primaryColor, DEFAULT_PRIMARY_COLOR),
    secondaryColor: colorValueOrDefault(branding?.secondaryColor, DEFAULT_SECONDARY_COLOR),
    publicAssetMode: normalizeEventPublicAssetMode(branding?.publicAssetMode),
    programPdfUrl: textValue(branding?.programPdfUrl),
    venueLayoutImageUrl: textValue(branding?.venueLayoutImageUrl),
    sponsorsImageUrl: textValue(branding?.sponsorsImageUrl),
    badgeTemplateUrl: textValue(branding?.badgeTemplateUrl),
    badgeBackTemplateUrl: textValue(branding?.badgeBackTemplateUrl),
    badgeFirstNameX: branding?.badgeFirstNameX ?? null,
    badgeFirstNameY: branding?.badgeFirstNameY ?? null,
    badgeLastNameX: branding?.badgeLastNameX ?? null,
    badgeLastNameY: branding?.badgeLastNameY ?? null,
    badgeCompanyX: branding?.badgeCompanyX ?? null,
    badgeCompanyY: branding?.badgeCompanyY ?? null,
    badgeQrCodeX: branding?.badgeQrCodeX ?? null,
    badgeQrCodeY: branding?.badgeQrCodeY ?? null,
    certificateTemplateUrl: textValue(branding?.certificateTemplateUrl),
    certificateParticipantNameX: branding?.certificateParticipantNameX ?? null,
    certificateParticipantNameY: branding?.certificateParticipantNameY ?? null
});

const buildEventBasicSetupInput = (eventId: number, values: EventBasicSetupFormValues) => {
    return {
        id: eventId,
        code: textValue(values.code) || `EVENT-${eventId}`,
        name: textValue(values.name),
        nameHr: textValue(values.nameHr),
        nameEn: textValue(values.nameEn),
        place: textValue(values.place),
        address: textValue(values.address),
        eventInformationHr: textValue(values.eventInformationHr),
        eventInformationEn: textValue(values.eventInformationEn),
        onSiteRegistrationInstructionsHr: textValue(values.onSiteRegistrationInstructionsHr),
        onSiteRegistrationInstructionsEn: textValue(values.onSiteRegistrationInstructionsEn),
        hasParticipationFee: values.hasParticipationFee ?? true,
        startDate: dayjs(values.startDate).startOf("day").toISOString(),
        endDate: dayjs(values.endDate).startOf("day").toISOString()
    };
};

// Badge/certificate placement is edited on the template pages; keep stored values when the basics form omits them.
const buildEventBasicSetupBrandingFields = (
    values: EventBasicSetupFormValues,
    branding?: EventBasicSetupBranding | null
) => ({
    logoUrl: textValue(values.logoUrl),
    primaryColor: textValue(values.primaryColor),
    secondaryColor: textValue(values.secondaryColor),
    publicAssetMode: normalizeEventPublicAssetMode(values.publicAssetMode),
    programPdfUrl: textValue(values.programPdfUrl),
    venueLayoutImageUrl: textValue(values.venueLayoutImageUrl),
    sponsorsImageUrl: textValue(values.sponsorsImageUrl),
    badgeTemplateUrl: firstNonEmptyText(values.badgeTemplateUrl, branding?.badgeTemplateUrl),
    badgeBackTemplateUrl: firstNonEmptyText(values.badgeBackTemplateUrl, branding?.badgeBackTemplateUrl),
    badgeFirstNameX: values.badgeFirstNameX ?? branding?.badgeFirstNameX ?? null,
    badgeFirstNameY: values.badgeFirstNameY ?? branding?.badgeFirstNameY ?? null,
    badgeLastNameX: values.badgeLastNameX ?? branding?.badgeLastNameX ?? null,
    badgeLastNameY: values.badgeLastNameY ?? branding?.badgeLastNameY ?? null,
    badgeCompanyX: values.badgeCompanyX ?? branding?.badgeCompanyX ?? null,
    badgeCompanyY: values.badgeCompanyY ?? branding?.badgeCompanyY ?? null,
    badgeQrCodeX: values.badgeQrCodeX ?? branding?.badgeQrCodeX ?? null,
    badgeQrCodeY: values.badgeQrCodeY ?? branding?.badgeQrCodeY ?? null,
    badgeTextFieldsJson: textValue(branding?.badgeTextFieldsJson),
    headerImageUrl: textValue(branding?.headerImageUrl),
    certificateTemplateUrl: firstNonEmptyText(values.certificateTemplateUrl, branding?.certificateTemplateUrl),
    certificateParticipantNameX: values.certificateParticipantNameX ?? branding?.certificateParticipantNameX ?? null,
    certificateParticipantNameY: values.certificateParticipantNameY ?? branding?.certificateParticipantNameY ?? null,
    certificateTextFieldsJson: textValue(branding?.certificateTextFieldsJson)
});

export const buildEventBasicSetupSaveInput = (
    eventId: number,
    values: EventBasicSetupFormValues,
    branding?: EventBasicSetupBranding | null
) => ({
    ...buildEventBasicSetupInput(eventId, values),
    ...buildEventBasicSetupBrandingFields(values, branding)
});

// Tax

export type EventTaxSettingFormValues = {
    registrationVatPercent: number | string;
    accommodationPpoPercent: number | string;
};

export const defaultTaxSettingFormValues = (): EventTaxSettingFormValues => ({
    registrationVatPercent: 25,
    accommodationPpoPercent: 0
});

export const eventTaxSettingToFormValues = (
    taxSetting?: EventTaxSettingReturnDto | null
): EventTaxSettingFormValues => ({
    registrationVatPercent: taxRateToPercent(taxSetting?.registrationVatRate ?? 0.25),
    accommodationPpoPercent: taxRateToPercent(taxSetting?.accommodationVatRate ?? 0)
});

export const buildEventTaxSettingInput = (
    eventId: number,
    values: EventTaxSettingFormValues
) => ({
    eventId,
    registrationVatRate: percentToTaxRate(Number(values.registrationVatPercent)),
    accommodationVatRate: percentToTaxRate(Number(values.accommodationPpoPercent)),
    accommodationTaxScheme: AccommodationTaxScheme.Ppo
});

// Payment methods

export const eventPaymentMethodCategories: Array<{
    category: EventPaymentMethodCategory;
    title: string;
    description: string;
}> = [
    {
        category: PaymentMethodCategory.Participation,
        title: "Participation and options",
        description: "Used for participation fees and paid registration options."
    },
    {
        category: PaymentMethodCategory.Accommodation,
        title: "Accommodation",
        description: "Used for hotel and room reservation offers."
    }
];

export const createDefaultPaymentMethodState = () => ({
    PARTICIPATION: {
        bankTransferAllowed: true,
        cardAllowed: true,
        posAllowed: false
    },
    ACCOMMODATION: {
        bankTransferAllowed: true,
        cardAllowed: true,
        posAllowed: false
    }
});

export type EventPaymentMethodState = ReturnType<typeof createDefaultPaymentMethodState>;
export type EventPaymentMethodToggleKey = keyof EventPaymentMethodState[EventPaymentMethodCategory];

export const eventPaymentMethodsToState = (
    items: Array<EventPaymentMethodConfig | null | undefined>
): EventPaymentMethodState => {
    const next = createDefaultPaymentMethodState();

    items.forEach(item => {
        if (!item || !(item.category in next)) {
            return;
        }

        next[item.category] = {
            bankTransferAllowed: item.bankTransferAllowed,
            cardAllowed: item.cardAllowed,
            posAllowed: item.posAllowed
        };
    });

    return next;
};

export const hasAnyPaymentMethodEnabled = (state: EventPaymentMethodState[EventPaymentMethodCategory]) =>
    state.bankTransferAllowed || state.cardAllowed || state.posAllowed;

// Pricing periods and fee types

export type PricePeriodValidationLabels = {
    startDateRequired: string;
    endDateRequired: string;
    endBeforeStart: string;
    overlap: (period: string, startDate: string, endDate: string) => string;
};

const pricePeriodDateOrNull = (value: unknown) => {
    if (!value) {
        return null;
    }

    const parsed = dayjs(value as any).startOf("day");
    return parsed.isValid() ? parsed : null;
};

const formatPricePeriodDate = (value: unknown) =>
    pricePeriodDateOrNull(value)?.format("YYYY-MM-DD") ?? "";

const normalizePricePeriodFormDate = (value?: string | null): string | undefined => {
    if (!value) {
        return undefined;
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
};

export const buildDefaultPricePeriodFormData = ({
    eventId,
    eventStartDate,
    eventEndDate
}: {
    eventId?: number;
    eventStartDate?: string | null;
    eventEndDate?: string | null;
}): Partial<EventPricePeriodCreateDtoInput> => {
    const startDate = normalizePricePeriodFormDate(eventStartDate) ?? dayjs().format("YYYY-MM-DD");
    const normalizedEndDate = normalizePricePeriodFormDate(eventEndDate);
    const endDate = normalizedEndDate && !dayjs(normalizedEndDate).isBefore(dayjs(startDate), "day")
        ? normalizedEndDate
        : startDate;

    return {
        eventId,
        startDate,
        endDate
    };
};

export const pricePeriodDisplayName = (pricePeriod: Partial<EventPricePeriodReturnDto>) =>
    textValue(pricePeriod.name) || textValue(pricePeriod.code) || `#${pricePeriod.id}`;

const pricePeriodCodeFromName = (value: unknown) => {
    const code = textValue(String(value ?? ""))
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    return code || "period";
};

export const buildPricePeriodInput = (
    values: Record<string, unknown>,
    eventId: number | undefined,
    existingPricePeriods: Array<EventPricePeriodReturnDto | null | undefined>,
    labels: PricePeriodValidationLabels,
    id?: number
) => {
    const existing = id ? findById(existingPricePeriods, id) : undefined;
    const startDate = pricePeriodDateOrNull(values.startDate);
    const endDate = pricePeriodDateOrNull(values.endDate);
    const errors: Record<string, string[]> = {};

    if (!startDate) {
        errors.startDate = [labels.startDateRequired];
    }

    if (!endDate) {
        errors.endDate = [labels.endDateRequired];
    }

    if (startDate && endDate && endDate.valueOf() < startDate.valueOf()) {
        errors.endDate = [labels.endBeforeStart];
    }

    const periodEventId = Number(values.eventId ?? eventId);
    if (startDate && endDate && !errors.endDate) {
        const overlappingPeriod = existingPricePeriods.find(pricePeriod => {
            if (!pricePeriod || pricePeriod.id === id || pricePeriod.eventId !== periodEventId) {
                return false;
            }

            const existingStartDate = pricePeriodDateOrNull(pricePeriod.startDate);
            const existingEndDate = pricePeriodDateOrNull(pricePeriod.endDate);

            return !!existingStartDate &&
                !!existingEndDate &&
                startDate.valueOf() <= existingEndDate.valueOf() &&
                endDate.valueOf() >= existingStartDate.valueOf();
        });

        if (overlappingPeriod) {
            const message = labels.overlap(
                pricePeriodDisplayName(overlappingPeriod),
                formatPricePeriodDate(overlappingPeriod.startDate),
                formatPricePeriodDate(overlappingPeriod.endDate)
            );
            errors.startDate = [message];
            errors.endDate = [message];
        }
    }

    if (Object.keys(errors).length > 0) {
        throw {errors};
    }

    return {
        ...(id ? {id} : {}),
        eventId: periodEventId,
        name: formTextValue(values.name, existing?.name),
        code: formTextValue(values.code, existing?.code ?? pricePeriodCodeFromName(values.name)),
        startDate: startDate!.toISOString(),
        endDate: endDate!.toISOString()
    };
};

export const buildFeeTypeCreateInput = (
    values: Record<string, unknown>,
    eventId: number | undefined
): EventFeeTypeCreateDtoInput => ({
    eventId: Number(eventId ?? values.eventId),
    labelHr: formTextValue(values.labelHr),
    labelEn: formTextValue(values.labelEn),
    isFree: formBooleanValue(values.isFree),
    isSponsored: formBooleanValue(values.isSponsored),
    isPublicRegistrationHidden: formBooleanValue(values.isPublicRegistrationHidden),
    requiresParticipantTypeProof: formBooleanValue(values.requiresParticipantTypeProof)
});

export const buildFeeTypeUpdateInput = (
    values: Record<string, unknown>,
    eventId: number | undefined,
    existingFeeTypes: Array<EventFeeTypeReturnDto | null | undefined>
): EventFeeTypeUpdateDtoInput => {
    const existing = findById(existingFeeTypes, values.id);

    return {
        id: Number(values.id),
        eventId: Number(eventId ?? values.eventId ?? existing?.eventId),
        labelHr: formTextValue(values.labelHr, existing?.labelHr),
        labelEn: formTextValue(values.labelEn, existing?.labelEn),
        isFree: formBooleanValue(values.isFree, existing?.isFree ?? false),
        isSponsored: formBooleanValue(values.isSponsored, existing?.isSponsored ?? false),
        isPublicRegistrationHidden: formBooleanValue(
            values.isPublicRegistrationHidden,
            existing?.isPublicRegistrationHidden ?? false),
        requiresParticipantTypeProof: formBooleanValue(
            values.requiresParticipantTypeProof,
            existing?.requiresParticipantTypeProof ?? false)
    };
};

export const joinPrerequisiteLabels = (labels: string[]) => {
    if (labels.length <= 1) {
        return labels[0] ?? "";
    }

    if (labels.length === 2) {
        return `${labels[0]} and ${labels[1]}`;
    }

    return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
};

// Activities

export const isChoiceActivityQuestionType = (value: unknown) => {
    const normalized = String(value ?? "")
        .replace(/[_\s-]+/g, "")
        .toLowerCase();

    return normalized === "singlechoice" || normalized === "multichoice";
};

// Accommodation

export type AccommodationOptionFormState = {
    allotmentId?: number;
    roomTypeId?: number;
    hotelSelection: string;
    roomTypeName: string;
    capacity: string;
    pricePerNight: string;
    description: string;
    startDate: string;
    endDate: string;
    totalRooms: string;
    paymentDeadline: string;
};

export type AccommodationOptionRow = {
    rowId: string;
    allotmentId?: number;
    roomTypeId?: number;
    hotelId?: number;
    hotelName: string;
    roomTypeName: string;
    capacity: number | string;
    pricePerNight: number | string;
    description: string;
    startDate: string;
    endDate: string;
    totalRooms: number | string;
    paymentDeadline?: string | null;
    reservationCount: number;
};

export type HotelCatalogFormValues = {
    name: string;
    address: string;
    city: string;
    country: string;
};

export const EVENT_HOTEL_SELECTION_PREFIX = "event:";
export const CATALOG_HOTEL_SELECTION_PREFIX = "catalog:";

export const createDefaultAccommodationOptionForm = (
    eventStartDate?: string | null,
    eventEndDate?: string | null,
    hotelSelection = ""
): AccommodationOptionFormState => ({
    hotelSelection,
    roomTypeName: "",
    capacity: "1",
    pricePerNight: "0",
    description: "",
    startDate: dateInputValue(eventStartDate),
    endDate: dateInputValue(eventEndDate),
    totalRooms: "1",
    paymentDeadline: dayjs().format("YYYY-MM-DD")
});

export const parseAccommodationHotelSelection = (value: string) => {
    if (value.startsWith(EVENT_HOTEL_SELECTION_PREFIX)) {
        return {
            type: "event" as const,
            id: Number(value.slice(EVENT_HOTEL_SELECTION_PREFIX.length))
        };
    }

    if (value.startsWith(CATALOG_HOTEL_SELECTION_PREFIX)) {
        return {
            type: "catalog" as const,
            id: Number(value.slice(CATALOG_HOTEL_SELECTION_PREFIX.length))
        };
    }

    return null;
};

export const buildHotelCreateInput = (
    values: Record<string, unknown>,
    eventId: number | undefined
): EventHotelCreateDtoInput => ({
    eventId: Number(eventId ?? values.eventId),
    name: formTextValue(values.name),
    category: formTextValue(values.category),
    address: formTextValue(values.address),
    city: formTextValue(values.city),
    country: formTextValue(values.country),
    notes: formTextValue(values.notes)
});

export const mergeEventHotels = (
    queryHotels: Array<EventHotelReturnDto | null | undefined>,
    inlineHotels: EventHotelReturnDto[]
): EventHotelReturnDto[] => {
    const hotelsById = new Map<number, EventHotelReturnDto>();

    [...queryHotels, ...inlineHotels].forEach(hotel => {
        if (hotel?.id) {
            hotelsById.set(hotel.id, hotel);
        }
    });

    return Array.from(hotelsById.values())
        .sort((a, b) => a.name.localeCompare(b.name, undefined, {sensitivity: "base"}) || a.id - b.id);
};

export const mergeById = <TItem extends { id: number }>(
    queryItems: Array<TItem | null | undefined>,
    inlineItems: TItem[]
): TItem[] => {
    const itemsById = new Map<number, TItem>();

    [...queryItems, ...inlineItems].forEach(item => {
        if (item?.id) {
            itemsById.set(item.id, item);
        }
    });

    return Array.from(itemsById.values()).sort((a, b) => a.id - b.id);
};

export const firstMutationItem = <TItem, >(items?: Array<TItem | null> | null): TItem | undefined =>
    items?.find((item): item is TItem => !!item);

const normalizeComparableHotelText = (value?: string | null): string =>
    (value ?? "").trim().toLocaleLowerCase();

export const findMatchingEventHotel = (
    items: Array<EventHotelReturnDto | null | undefined>,
    hotelName: string,
    form: HotelCatalogFormValues
): EventHotelReturnDto | undefined => {
    const expectedName = normalizeComparableHotelText(hotelName);
    const expectedAddress = normalizeComparableHotelText(form.address);
    const expectedCity = normalizeComparableHotelText(form.city);
    const expectedCountry = normalizeComparableHotelText(form.country);

    return items
        .filter((item): item is EventHotelReturnDto => {
            if (!item || normalizeComparableHotelText(item.name) !== expectedName) {
                return false;
            }

            return (!expectedAddress || normalizeComparableHotelText(item.address) === expectedAddress)
                && (!expectedCity || normalizeComparableHotelText(item.city) === expectedCity)
                && (!expectedCountry || normalizeComparableHotelText(item.country) === expectedCountry);
        })
        .sort((a, b) => b.id - a.id)[0];
};
